import { template as template_8035e1ea5aa74288954e46f2a822cee8 } from "@ember/template-compiler";
import Component from '@glimmer/component';
import { task } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import { provide } from 'ember-provide-consume-context';
import { CardCrudFunctionsContextName, type CommandContext } from '@cardstack/runtime-common';
import type { StackItem } from '@cardstack/host/lib/stack-item';
import type { CreateCardFn, DeleteCardFn, EditCardFn, SaveCardFn, ViewCardFn } from 'https://cardstack.com/base/card-api';
import OperatorModeStackItem, { type StackItemComponentAPI } from './stack-item';
import type { CardDefOrId } from './stack-item';
interface Signature {
    Element: HTMLElement;
    Args: {
        tag?: keyof HTMLElementTagNameMap;
        stackItems: StackItem[];
        stackIndex: number;
        createCard: CreateCardFn;
        viewCard: ViewCardFn;
        editCard: EditCardFn;
        saveCard: SaveCardFn;
        deleteCard: DeleteCardFn;
        commandContext: CommandContext;
        close: (stackItem: StackItem) => void;
        onSelectedCards: (selectedCards: CardDefOrId[], stackItem: StackItem) => void;
        setupStackItem: (model: StackItem, componentAPI: StackItemComponentAPI) => void;
    };
    Blocks: {
    };
}
export default class OperatorModeStack extends Component<Signature> {
    private stackItemComponentAPI = new WeakMap<StackItem, StackItemComponentAPI>();
    @provide(CardCrudFunctionsContextName)
    private get cardCrudFunctions(): CardCrudFunctions {
        return {
            createCard: this.args.createCard,
            saveCard: this.args.saveCard,
            editCard: this.args.editCard,
            viewCard: this.args.viewCard,
            deleteCard: this.args.deleteCard
        };
    }
    dismissStackedCardsAbove = task(async (itemIndex: number)=>{
        let itemsToDismiss: StackItem[] = [];
        for(let i = this.args.stackItems.length - 1; i > itemIndex; i--){
            itemsToDismiss.push(this.args.stackItems[i]);
        }
        // Animate closing items
        const animations = itemsToDismiss.map((item)=>{
            const componentAPI = this.stackItemComponentAPI.get(item);
            return componentAPI?.startAnimation('closing') ?? undefined;
        }).filter(Boolean);
        // Animate next top item moving forward
        const nextTopItem = this.args.stackItems[itemIndex];
        const nextTopItemAPI = this.stackItemComponentAPI.get(nextTopItem);
        if (nextTopItemAPI) {
            animations.push(nextTopItemAPI.startAnimation('movingForward'));
        }
        await Promise.all(animations);
        await Promise.all(itemsToDismiss.map((i)=>this.args.close(i)));
    });
    private setupStackItem = (item: StackItem, componentAPI: StackItemComponentAPI)=>{
        this.args.setupStackItem(item, componentAPI);
        this.stackItemComponentAPI.set(item, componentAPI);
    };
    static{
        template_8035e1ea5aa74288954e46f2a822cee8(`
    <div class='operator-mode-stack' ...attributes>
      <div class='inner'>
        {{#each @stackItems as |item i|}}
          <OperatorModeStackItem
            @item={{item}}
            @index={{i}}
            @stackItems={{@stackItems}}
            @commandContext={{@commandContext}}
            @dismissStackedCardsAbove={{perform this.dismissStackedCardsAbove}}
            @requestDeleteCard={{@deleteCard}}
            @close={{@close}}
            @onSelectedCards={{@onSelectedCards}}
            @setupStackItem={{this.setupStackItem}}
          />
        {{/each}}
      </div>
    </div>

    <style scoped>
      :global(:root) {
        --stack-padding-top: calc(
          var(--operator-mode-top-bar-item-height) +
            (2 * (var(--operator-mode-spacing)))
        );
        --stack-padding-bottom: var(--boxel-sp-lg);
      }
      .operator-mode-stack {
        z-index: 0;
        height: 100%;
        width: 100%;
        background-position: center;
        background-size: cover;
        padding-top: var(--stack-padding-top);
        padding-inline: var(--operator-mode-spacing);
        padding-bottom: var(--stack-padding-bottom);
        position: relative;
        transition: padding-top var(--boxel-transition);
      }
      .operator-mode-stack
        :deep(.field-component-card.fitted-format .missing-template) {
        margin-top: calc(-1 * var(--boxel-sp-lg));
        border-radius: 0;
        border-bottom-left-radius: var(--boxel-form-control-border-radius);
        border-bottom-right-radius: var(--boxel-form-control-border-radius);
      }
      .inner {
        height: 100%;
        position: relative;
        display: flex;
        justify-content: center;
        margin: 0 auto;
        border-bottom-left-radius: var(--boxel-border-radius);
        border-bottom-right-radius: var(--boxel-border-radius);
      }
    </style>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
